import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  BookOpen,
  CalendarDays,
  CheckCircle2,
  Building2,
  Zap,
  ArrowRight,
  Loader2,
  TrendingUp,
  Layers,
  Wrench,
  Home,
  AlertCircle,
  Clock,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { leccionesAprendidasAPI, proyectosAPI } from '../../utils/api';
import { formatFechaLima } from '../../utils/date';
import { toast } from 'sonner';

// Paleta mate del mockup (orden = orden de rotación en pies/barras)
const PALETTE = ['#9d8272', '#7a9e5f', '#c0855a', '#b85555', '#c5b8a2', '#8a7162'];

// Acentos semánticos para KPI cards (border-top y color del valor/icono)
const ACCENT = {
  bronce:  { bar: '#9d8272', strong: '#5a4a3a' },
  verde:   { bar: '#7a9e5f', strong: '#4a7a28' },
  naranja: { bar: '#c0855a', strong: '#8c5020' },
  rojo:    { bar: '#b85555', strong: '#8c3020' },
  beige:   { bar: '#c5b8a2', strong: '#7a6a55' },
};

const fmtFecha = (s) => {
  if (!s) return '—';
  return formatFechaLima(s, { day: '2-digit', month: '2-digit', year: '2-digit' }) || s;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Base path = pathname sin el segmento `/dashboard`
  const basePath = useMemo(
    () => location.pathname.replace(/\/dashboard\/?$/, ''),
    [location.pathname]
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proyectos, setProyectos] = useState([]);
  const [filterProyecto, setFilterProyecto] = useState('all');
  const [topLectores, setTopLectores] = useState({ items: [], total_universo: 0 });

  // Cargar proyectos para el filtro
  useEffect(() => {
    proyectosAPI
      .getAll()
      .then((r) => {
        const arr = Array.isArray(r.data) ? r.data : r.data?.items || [];
        setProyectos(arr.filter((p) => p.activo !== false));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = filterProyecto === 'all' ? {} : { proyecto_id: filterProyecto };
    leccionesAprendidasAPI
      .reportsOverview(params)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Error al cargar el dashboard'))
      .finally(() => setLoading(false));
  }, [filterProyecto]);

  // Cargar Top 5 Lectores (global, no depende de filterProyecto)
  useEffect(() => {
    leccionesAprendidasAPI
      .topLectores(5)
      .then((r) => setTopLectores(r.data || { items: [], total_universo: 0 }))
      .catch(() => {});
  }, []);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-[#9d8272]" />
      </div>
    );
  }

  const { kpis, trend_mensual, por_especialidad, por_etapa_deteccion, top_proyectos, por_tipo_impacto, recientes } = data;

  return (
    <div className="space-y-6" data-testid="la-dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1
            className="text-2xl sm:text-4xl font-bold text-[#2D2D2D]"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Dashboard de Lecciones Aprendidas
          </h1>
          <p className="text-sm sm:text-lg text-[#5A5A5A] mt-1">
            Vista ejecutiva de aprendizajes registrados en obra
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div className="min-w-[220px]">
            <Label className="text-xs text-[#5A5A5A]">Proyecto</Label>
            <Select value={filterProyecto} onValueChange={setFilterProyecto}>
              <SelectTrigger className="h-9" data-testid="filter-proyecto">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los proyectos</SelectItem>
                {proyectos.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.codigo_proyecto ? `${p.codigo_proyecto} · ` : ''}
                    {p.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate(basePath)}
            className="self-end"
            data-testid="back-to-list-btn"
          >
            Ver listado
            <ArrowRight size={14} className="ml-2" />
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-grid">
        <Kpi
          icon={<BookOpen size={18} />}
          label="Total lecciones"
          value={kpis.total}
          accent={ACCENT.bronce}
          testId="kpi-total"
        />
        <Kpi
          icon={<CalendarDays size={18} />}
          label="Este mes"
          value={kpis.este_mes}
          accent={ACCENT.verde}
          testId="kpi-mes"
        />
        <Kpi
          icon={<CheckCircle2 size={18} />}
          label="Leídas por mí"
          value={kpis.leidas_por_mi}
          sub={kpis.total > 0 ? `${Math.round((kpis.leidas_por_mi / kpis.total) * 100)}%` : '0%'}
          accent={ACCENT.verde}
          testId="kpi-leidas"
        />
        {kpis.sin_leer_por_mi > 0 ? (
          <button
            onClick={() => navigate(`${basePath}?estado_lectura=unread`)}
            className="bg-[#fff8ec] border border-amber-200 hover:border-amber-300 hover:shadow-sm rounded-lg p-4 text-left transition group border-t-[3px]"
            style={{ borderTopColor: '#c0855a' }}
            data-testid="cta-sin-leer"
          >
            <div className="flex items-center gap-2 text-amber-700">
              <BookOpen size={18} />
              <span className="text-xs uppercase tracking-wider">
                Sin leer
              </span>
            </div>
            <div className="mt-2 flex items-baseline justify-between gap-2">
              <span className="text-3xl font-bold text-amber-900">
                {kpis.sin_leer_por_mi}
              </span>
              <span className="text-xs text-amber-700 group-hover:translate-x-0.5 transition">
                Ver →
              </span>
            </div>
          </button>
        ) : (
          <Kpi
            icon={<CheckCircle2 size={18} />}
            label="Sin leer"
            value={0}
            sub="¡Al día!"
            accent={ACCENT.verde}
            testId="kpi-sin-leer-cero"
          />
        )}
        <Kpi
          icon={<Building2 size={18} />}
          label="Proyectos involucrados"
          value={kpis.proyectos_distintos}
          accent={ACCENT.beige}
          testId="kpi-proyectos"
        />
        <Kpi
          icon={<Zap size={18} />}
          label="Impactos registrados"
          value={kpis.impactos_registrados}
          accent={ACCENT.rojo}
          testId="kpi-impactos"
        />
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card
          title="Lecciones por mes"
          subtitle="Últimos 12 meses"
          icon={<TrendingUp size={14} />}
        >
          {trend_mensual.every((m) => m.count === 0) ? (
            <EmptyChart text="Sin lecciones en los últimos 12 meses" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={trend_mensual} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E7" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: '#5A5A5A' }}
                  interval={0}
                  angle={-30}
                  textAnchor="end"
                  height={50}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#5A5A5A' }} />
                <Tooltip
                  contentStyle={{ background: '#fff', border: '1px solid #E8E8E7', borderRadius: 6, fontSize: 12 }}
                  formatter={(v) => [v, 'Lecciones']}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {trend_mensual.map((_, i) => (
                    <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Por etapa de detección" icon={<Layers size={14} />}>
          {por_etapa_deteccion.length === 0 ? (
            <EmptyChart text="Sin datos para mostrar" />
          ) : (
            <DonutWithLegend
              data={por_etapa_deteccion}
              testId="donut-etapa-deteccion"
            />
          )}
        </Card>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Por especialidad" icon={<Wrench size={14} />}>
          {por_especialidad.length === 0 ? (
            <EmptyChart text="Sin datos para mostrar" />
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, por_especialidad.length * 32)}>
              <BarChart
                data={por_especialidad}
                layout="vertical"
                margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E7" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#5A5A5A' }} />
                <YAxis
                  type="category"
                  dataKey="key"
                  width={130}
                  tick={{ fontSize: 11, fill: '#5A5A5A' }}
                />
                <Tooltip
                  contentStyle={{ background: '#fff', border: '1px solid #E8E8E7', borderRadius: 6, fontSize: 12 }}
                  formatter={(v) => [v, 'Lecciones']}
                />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {por_especialidad.map((_, i) => (
                    <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card
          title="Top 5 proyectos con más lecciones"
          icon={<Home size={14} />}
        >
          {top_proyectos.length === 0 ? (
            <EmptyChart text="Sin lecciones registradas aún" />
          ) : (
            <div className="space-y-2 py-2" data-testid="top-proyectos">
              {top_proyectos.map((p, i) => {
                const max = top_proyectos[0]?.count || 1;
                const pct = (p.count / max) * 100;
                return (
                  <div key={p.proyecto_id} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-[#2D2D2D] truncate">
                        {p.codigo && (
                          <span className="text-[#9d8272] font-mono text-xs mr-1">
                            {p.codigo}
                          </span>
                        )}
                        {p.nombre}
                      </span>
                      <span className="text-[#9d8272] font-bold ml-2">{p.count}</span>
                    </div>
                    <div className="h-2 bg-[#f5f1ea] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${pct}%`,
                          background: PALETTE[i % PALETTE.length],
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Charts row 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-4 flex flex-col">
          <Card title="Por tipo de impacto" icon={<AlertCircle size={14} />}>
            {por_tipo_impacto.length === 0 ? (
              <EmptyChart text="Sin impactos registrados" />
            ) : (
              <DonutWithLegend
                data={por_tipo_impacto}
                testId="donut-tipo-impacto"
              />
            )}
          </Card>

          <Card
            title="Top 5 Lectores"
            icon={<BookOpen size={14} />}
            subtitle={
              <button
                onClick={() => navigate(`${basePath}/lectores`)}
                className="text-xs text-[#9d8272] hover:text-[#8a7162] font-medium flex items-center gap-1"
                data-testid="link-ver-todos-lectores"
              >
                Ver todos <ArrowRight size={12} />
              </button>
            }
          >
            {(!topLectores.items || topLectores.items.length === 0) ? (
              <EmptyChart text="Aún no hay lectores registrados" />
            ) : (
              <div className="divide-y divide-[#E8E8E7]" data-testid="top-lectores-list">
                {topLectores.items.map((u, i) => (
                  <div
                    key={u.user_id}
                    className="py-2 px-1 flex items-center gap-3"
                    data-testid={`top-lector-row-${i}`}
                  >
                    <div className="w-6 h-6 rounded-full bg-[#f5efe6] text-[#9d8272] flex items-center justify-center text-xs font-bold shrink-0">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#2D2D2D] truncate">
                        {u.nombre}
                      </div>
                      <div className="text-[10px] text-[#888888]">
                        {u.leidas} / {u.total_lecciones} lecciones
                      </div>
                    </div>
                    <div
                      className={`text-sm font-semibold tabular-nums w-14 text-right ${
                        u.semaforo === 'verde'
                          ? 'text-[#4a7a28]'
                          : u.semaforo === 'ambar'
                          ? 'text-[#c0855a]'
                          : 'text-[#b85555]'
                      }`}
                    >
                      {u.porcentaje}%
                    </div>
                    <SemaforoDot color={u.semaforo} />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <Card title="Últimas 5 lecciones" icon={<Clock size={14} />}>
          {recientes.length === 0 ? (
            <EmptyChart text="Aún no hay lecciones registradas" />
          ) : (
            <div className="divide-y divide-[#E8E8E7]" data-testid="recientes-list">
              {recientes.map((r) => (
                <button
                  key={r.id}
                  onClick={() => navigate(`${basePath}/${r.id}`)}
                  className="w-full text-left py-2 px-2 hover:bg-[#fafaf8] rounded transition"
                  data-testid={`reciente-${r.codigo}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[#9d8272] font-mono font-bold text-sm">
                      {r.codigo}
                    </span>
                    <span className="text-xs text-[#888888] whitespace-nowrap">
                      {fmtFecha(r.fecha_identificacion)}
                    </span>
                  </div>
                  <div className="text-xs text-[#5A5A5A] mt-1 line-clamp-2">
                    {r.descripcion_situacion}
                  </div>
                  <div className="text-[10px] text-[#888888] mt-1 flex gap-2">
                    <span>{r.proyecto_nombre}</span>
                    <span>·</span>
                    <span>{r.etapa_deteccion}</span>
                    <span>·</span>
                    <span>{r.especialidad}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

// ---------- helper components ----------
const Kpi = ({ icon, label, value, sub, accent = ACCENT.bronce, testId }) => (
  <div
    className="bg-white border border-[#E8E8E7] rounded-lg p-4 border-t-[3px]"
    style={{ borderTopColor: accent.bar }}
    data-testid={testId}
  >
    <div className="flex items-center gap-2" style={{ color: accent.bar }}>
      {icon}
      <span className="text-xs text-[#888888] uppercase tracking-wider">
        {label}
      </span>
    </div>
    <div className="mt-2 flex items-baseline gap-2">
      <span className="text-3xl font-bold" style={{ color: accent.strong }}>
        {value}
      </span>
      {sub && <span className="text-xs text-[#888888]">{sub}</span>}
    </div>
  </div>
);

const Card = ({ title, subtitle, icon, children }) => (
  <div className="bg-white border border-[#E8E8E7] rounded-lg shadow-sm">
    <div className="px-4 py-2.5 bg-[#fbf7f0] border-b border-[#e8dfd0] flex items-center gap-2">
      {icon && <span className="text-[#9d8272] flex-shrink-0">{icon}</span>}
      <h3 className="text-sm font-semibold text-[#2D2D2D] truncate flex-1">
        {title}
      </h3>
      {subtitle && (
        <span className="text-xs text-[#888888] flex-shrink-0 ml-2 truncate">
          {subtitle}
        </span>
      )}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

// Donut + leyenda lateral con porcentajes (estilo mockup_reportes_v1.html)
const DonutWithLegend = ({ data, testId }) => {
  const total = data.reduce((s, it) => s + (it.count || 0), 0);
  return (
    <div className="flex gap-4 items-center" data-testid={testId}>
      <div className="relative w-[180px] h-[180px] flex-shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="count"
              nameKey="key"
              innerRadius={52}
              outerRadius={82}
              paddingAngle={2}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: '#fff',
                border: '1px solid #E8E8E7',
                borderRadius: 6,
                fontSize: 12,
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex-1 flex flex-col gap-2 min-w-0">
        {data.map((item, i) => {
          const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
          return (
            <div
              key={item.key}
              className="flex justify-between items-center gap-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className="w-2 h-2 rounded-sm flex-shrink-0"
                  style={{ background: PALETTE[i % PALETTE.length] }}
                />
                <span className="text-xs text-[#333] truncate">{item.key}</span>
              </div>
              <span className="text-xs font-medium text-[#333] flex-shrink-0">
                {item.count}{' '}
                <span className="text-[#a89d95] font-normal">{pct}%</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EmptyChart = ({ text }) => (
  <div className="flex items-center justify-center h-[200px] text-sm text-[#888888]">
    {text}
  </div>
);

const SEMAFORO_COLORS = {
  verde: '#4a7a28',
  ambar: '#c0855a',
  rojo: '#b85555',
};

export const SemaforoDot = ({ color = 'rojo', size = 12 }) => (
  <span
    data-testid={`semaforo-${color}`}
    className="inline-block rounded-full shrink-0"
    style={{
      width: size,
      height: size,
      background: SEMAFORO_COLORS[color] || SEMAFORO_COLORS.rojo,
      boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)',
    }}
  />
);

export default Dashboard;
